import { useState } from 'react';
import { useParams } from 'react-router';
import NumberFlow from '@number-flow/react';
import { DailyUsageChart } from '@/components/dashboard/daily-usage-chart';
import { unitLabel, unitTitle } from '@/lib/usage-unit';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DateRangePicker } from '@/components/date-range-picker';
import { useOrgDashboard } from './use-org-dashboard';
import { type RangePreset, type CustomDates } from '@/pages/dashboard/use-dashboard-metrics';
import { PageContainer } from '@/components/page-container';
import { ResetsOn } from '@/components/dashboard/resets-on';
import { useSidebarOrgUsageQuery, useQuotaWindowRollover } from '@/components/billing/queries';

export function OrgDashboardPage() {
  const { orgId = '' } = useParams<{ orgId: string }>();
  const [preset, setPreset] = useState<RangePreset>('period');
  // Same cache entry as the sidebar meter: no extra request.
  const orgUsage = useSidebarOrgUsageQuery(orgId || null).data;
  useQuotaWindowRollover(orgUsage?.resetsAt);
  const [custom, setCustom] = useState<CustomDates>({ start: '', end: '' });
  const [channelTab, setChannelTab] = useState<'all' | 'active' | 'disconnected'>('all');
  // Scope the whole dashboard to team workspaces, customer workspaces, or both.
  const [kindTab, setKindTab] = useState<'all' | 'team' | 'customer'>('all');
  const { data, loading } = useOrgDashboard(
    orgId,
    preset,
    custom,
    channelTab,
    kindTab === 'all' ? undefined : kindTab,
    orgUsage?.periodStartsAt,
  );

  // Org-wide channel counts keyed off the durable channelStatus lifecycle field
  // (active = connected). Mirrors the workspace dashboard Channels card filter.
  const channelsActive = data?.channels ?? 0;
  const channelsDisconnected = data?.channelsDisconnected ?? 0;
  const channelCounts = {
    all: channelsActive + channelsDisconnected,
    active: channelsActive,
    disconnected: channelsDisconnected,
  };

  // AIT-436: one number in the org's own meter. Actions have no inbound, so
  // the direction filters that used to sit on the card and the chart are gone.
  const unit = data?.usageUnit;
  const usedValue = data?.used ?? 0;

  return (
    <PageContainer variant="scroll" className="space-y-6">
      <header className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
      </header>

      <div className="flex flex-wrap items-center gap-4">
        <Tabs value={preset} onValueChange={(v) => setPreset(v as RangePreset)}>
          <TabsList>
            <TabsTrigger value="period">Billing period</TabsTrigger>
            <TabsTrigger value="today">Today</TabsTrigger>
            <TabsTrigger value="7d">7d</TabsTrigger>
            <TabsTrigger value="30d">30d</TabsTrigger>
            <TabsTrigger value="custom">Custom</TabsTrigger>
          </TabsList>
        </Tabs>

        {preset === 'custom' && <DateRangePicker value={custom} onChange={setCustom} />}

        {/* Channel-status + workspace-kind filters, grouped to the right. */}
        <Tabs className="md:ml-auto" value={channelTab} onValueChange={(v) => setChannelTab(v as typeof channelTab)}>
          <TabsList aria-label="Channels filter">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="disconnected">Disconnected</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Workspace-kind filter scopes the whole page to the org's own team
            workspaces vs its reseller customers (or both). */}
        <Tabs value={kindTab} onValueChange={(v) => setKindTab(v as typeof kindTab)}>
          <TabsList aria-label="Workspace kind filter">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="team">Workspaces</TabsTrigger>
            <TabsTrigger value="customer">Customers</TabsTrigger>
          </TabsList>
        </Tabs>

      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader className="flex min-h-8 flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle>Channels</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="text-3xl font-bold"
              role="status"
              aria-label={`Org channels count ${channelCounts[channelTab]}`}
            >
              <NumberFlow value={channelCounts[channelTab]} className="tabular-nums" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex min-h-8 flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle>{unitTitle(unit)}</CardTitle>
            {preset === 'period' && <ResetsOn resetsAt={orgUsage?.resetsAt} />}
          </CardHeader>
          <CardContent>
            <div
              className="text-3xl font-bold"
              role="status"
              aria-label={`Org ${unitLabel(unit)} count ${usedValue}`}
            >
              <NumberFlow value={usedValue} className="tabular-nums" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex min-h-8 flex-row items-center justify-between gap-2 space-y-0">
          <CardTitle>Daily {unitTitle(unit)}</CardTitle>
        </CardHeader>
        <CardContent>
          <DailyUsageChart data={data?.daily ?? []} unitLabel={unitLabel(unit)} loading={loading} />
        </CardContent>
      </Card>
    </PageContainer>
  );
}
